import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { extractErr } from "@/lib/api";
import { useStoreBase } from "@/lib/store";

export function StorefrontForgotPassword({ config }) {
  const { slug, base } = useStoreBase();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password/request-reset", { email, kind: "customer", store_slug: slug });
      setSent(true);
    } catch (err) {
      toast.error(extractErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12">
      <h1 className="font-display text-3xl font-bold text-stone-900">Forgot password</h1>
      <p className="text-stone-500 text-sm mt-1">We'll email a reset link for {config.store_info?.name || "your account"}.</p>
      {sent ? (
        <div className="mt-8 p-5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="customer-forgot-sent">
          <div className="font-semibold">Check your inbox</div>
          <p className="text-sm mt-1">If an account exists for {email}, a reset link is on its way. It expires in 60 minutes.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label>Email</Label>
            <Input required type="email" data-testid="customer-forgot-email" className="mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <Button type="submit" disabled={loading} data-testid="customer-forgot-submit" className="w-full h-11 btn-store-primary rounded-full">
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
      <div className="mt-6 text-sm text-center">
        <Link to={`${base}/account/login`} className="text-stone-900 underline" data-testid="customer-forgot-back">Back to sign in</Link>
      </div>
    </div>
  );
}

export function StorefrontResetPassword() {
  const { base } = useStoreBase();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Missing reset token"); return; }
    setLoading(true);
    try {
      await api.post("/auth/password/reset", { token, password });
      toast.success("Password updated. Please sign in.");
      nav(`${base}/account/login`);
    } catch (err) {
      toast.error(extractErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12">
      <h1 className="font-display text-3xl font-bold text-stone-900">Set a new password</h1>
      <p className="text-stone-500 text-sm mt-1">Choose a password you can use the next time you sign in.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label>New password</Label>
          <Input required type="password" minLength={6} data-testid="customer-reset-password" className="mt-1" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div>
          <Label>Confirm password</Label>
          <Input required type="password" minLength={6} data-testid="customer-reset-confirm" className="mt-1" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <Button type="submit" disabled={loading || !token} data-testid="customer-reset-submit" className="w-full h-11 btn-store-primary rounded-full">
          {loading ? "Updating…" : "Update password"}
        </Button>
        {!token ? <p className="text-sm text-red-600" data-testid="customer-reset-notoken">Use the link from your email to reset your password.</p> : null}
      </form>
      <div className="mt-6 text-sm text-center">
        <Link to={`${base}/account/login`} className="text-stone-900 underline">Back to sign in</Link>
      </div>
    </div>
  );
}
